import styles from "./styles.module.scss"
import { _t } from "@cruisen/i18n"
import ArrowForwardOutline from "~icons/eva/arrow-forward-outline"
import { datesFormats, formatDate, calculateNights } from "@cruisen/react-shared"
import { ErrorHandledImage } from "../../atoms/ErrorHandledImage"
import classNames from "classnames"

export const getYearAndFormattedDate = (stringDate: string) => {
  const date = new Date(stringDate)

  if (isNaN(date.getTime())) {
    return {
      year: null,
      shortenedDayMonth: null,
    }
  }

  const shortenedDayMonth = formatDate(date.toISOString(), datesFormats.shortenedDayMonth)

  return {
    year: date.getFullYear(),
    shortenedDayMonth,
  }
}

type BookedCruise = {
  image: string
  lineLogoImage: string
  lineName: string
  name: string
  startDate: string
  endDate: string
  ports: string[]
}

const BookedCruiseCard = ({
  cruise,
  onClick,
  lightBackground = false,
}: {
  cruise: BookedCruise
  onClick?: () => void
  lightBackground?: boolean
}) => {
  const startDate = getYearAndFormattedDate(cruise.startDate)
  const endDate = getYearAndFormattedDate(cruise.endDate)
  const nights = calculateNights(cruise.startDate, cruise.endDate)

  return (
    <div className={styles.bookedCruiseCard} data-testid="booked-cruise-card" onClick={onClick}>
      <ErrorHandledImage
        uri={cruise.image}
        enableCaching
        className={styles.cruiseImage}
        errorMessage={_t("imageError.cruise")}
      />
      <div className={styles.bookedCruiseContents}>
        <div className={classNames(styles.blurView, lightBackground && styles.lightBackground)}>
          <div className={styles.cruiseLogoContainer}>
            <img src={cruise.lineLogoImage} className={styles.cruiseLogo} alt="Cruise line logo" />
          </div>
          <div className={styles.heading}>
            <p className={styles.cruiseName}>{cruise.name}</p>
            <p className={styles.cruiseLineName}>{cruise.lineName}</p>
          </div>
          {nights !== 0 ? (
            <>
              <div className={styles.divider}></div>
              <p className={styles.daysAndNights}>
                {_t("bookedCruiseCard.daysAndNights", {
                  daysAmount: nights + 1,
                  nightsAmount: nights,
                })}
              </p>
            </>
          ) : null}
          {startDate.year !== null && endDate.year !== null && (
            <div className={styles.datesRow}>
              <div className={styles.startDateColumn}>
                <p className={styles.grayText}>{_t("bookedCruiseCard.from")}</p>
                <p className={styles.primaryText}>
                  {startDate.shortenedDayMonth}
                  {"\n"}
                  {startDate.year}
                </p>
              </div>
              <div className={styles.iconContainer}>
                <ArrowForwardOutline />
              </div>
              <div className={styles.endDateColumn}>
                <p className={styles.grayText}>{_t("bookedCruiseCard.to")}</p>
                <p className={classNames(styles.endDate, styles.primaryText)}>
                  {endDate.shortenedDayMonth}
                  {"\n"}
                  {endDate.year}
                </p>
              </div>
            </div>
          )}
          {cruise.ports && (
            <>
              <div className={styles.divider}></div>
              <p className={styles.portsTitle}>
                {_t("bookedCruiseCard.ports", { amount: cruise.ports.length })}
              </p>
              <p className={styles.portsList}>{cruise.ports.join(" · ")}</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default BookedCruiseCard
